import React, { Component } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import moment from 'moment';
import Api from '../../services/api';
import UserSessionService from '../../services/user-session.service';
import { t } from '../usage/usage-i18n';
import DeviceIcon from './device-icon';

// ---------------------------------------------------------------------------
// Item 7 — "Sessões ativas" de um usuário + "Encerrar esta sessão" / "Desconectar de tudo". Só GlobalAdministrator (a API recusa
// os demais). Usado na aba Logins e sessões (usuário escolhido) e na edição de usuário. Carrega sozinho ao montar; o pai troca
// a key para recarregar. Toda ação pede confirmação e aceita um motivo (fica em tb_user_session.revoke_reason).
//   props: intl, userId, userName?, compact? (só o botão "Desconectar de tudo"), onNotify(variant, message)
// ---------------------------------------------------------------------------

const REASON_FALLBACK = {
  logout: 'Saiu (logout)', admin_session: 'Encerrada pelo administrador', admin_all: 'Desconectado de tudo pelo administrador',
  password_changed: 'Senha alterada', password_changed_by_admin: 'Senha alterada pelo administrador', user_deactivated: 'Usuário desativado',
  roles_changed: 'Papel alterado', refresh_reuse: 'Renovação reapresentada (possível roubo)', expired_absolute: 'Sessão passou de 24 h',
  inactive: 'Inatividade (1 h)', user_inactive: 'Usuário inativo', superseded: 'Substituída por um login mais novo', self_session: 'Encerrada pelo próprio usuário',
};

const fmt = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm:ss') : '—');
const shortUa = (ua) => {
  if (!ua) return '—';
  const m = ua.match(/(Edg|Chrome|Firefox|Safari|OPR)\/([\d.]+)/);
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return m ? `${m[1] === 'Edg' ? 'Edge' : m[1]} ${m[2].split('.')[0]}${os ? ` · ${os}` : ''}` : ua.slice(0, 40);
};

export default class ActiveSessions extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, rows: [], error: null, confirm: null, reason: '', working: false };
    this.load = this.load.bind(this);
    this.doConfirm = this.doConfirm.bind(this);
  }

  componentDidMount() { this.load(); }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }

  load() {
    const { userId } = this.props;
    if (!userId) { this.setState({ loading: false, rows: [] }); return; }
    this.setState({ loading: true });
    UserSessionService.list(userId, false, 50)
      .then((rows) => this.setState({ loading: false, rows, error: null }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false, rows: [], error: err.message }); });
  }

  reasonLabel(code) {
    const base = String(code || '').split(':')[0];
    const extra = String(code || '').indexOf(':') >= 0 ? String(code).slice(String(code).indexOf(':') + 1) : '';
    const label = this.tr(`session.reason.${base}`, REASON_FALLBACK[base] || base);
    return extra ? `${label} · ${extra}` : label;
  }

  doConfirm() {
    const { confirm, reason } = this.state;
    if (!confirm) return;
    this.setState({ working: true });
    const call = confirm.kind === 'all' ? UserSessionService.revokeAll(this.props.userId, reason) : UserSessionService.revoke(confirm.sessionId, reason);
    call
      .then((data) => {
        const n = confirm.kind === 'all' ? Number(data && data.revoked) || 0 : 1;
        if (this.props.onNotify) this.props.onNotify('success', confirm.kind === 'all' ? this.tr('session.revokedAll', `Usuário desconectado de tudo (${n} sessão(ões)).`).replace('{n}', n) : this.tr('session.revokedOne', 'Sessão encerrada.'));
        this.setState({ confirm: null, reason: '', working: false }, this.load);
      })
      .catch((err) => { Api.kickoff(err); this.setState({ working: false }); if (this.props.onNotify) this.props.onNotify('error', err.message); });
  }

  renderConfirm() {
    const { confirm, reason, working } = this.state;
    if (!confirm) return null;
    const all = confirm.kind === 'all';
    return (
      <Dialog open onClose={() => (working ? null : this.setState({ confirm: null }))} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
        <DialogTitle>{all ? this.tr('session.revokeAll.title', 'Desconectar de tudo?') : this.tr('session.revokeOne.title', 'Encerrar esta sessão?')}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ padding: '16px 20px' }}>
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              {all
                ? this.tr('session.revokeAll.text', 'Todas as sessões abertas caem agora e nenhum token emitido antes deste instante vale mais, em nenhuma API. A pessoa precisará fazer login de novo.')
                : this.tr('session.revokeOne.text', 'A pessoa cai na próxima requisição e este navegador não consegue mais renovar. Outras sessões dela continuam.')}
            </Typography>
            <TextField fullWidth size="small" label={this.tr('session.reason', 'Motivo (opcional, fica registrado)')} value={reason} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 30 }} onChange={(e) => this.setState({ reason: e.target.value })} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ confirm: null })} disabled={working}>{this.tr('cancel', 'Cancelar')}</Button>
          <Button variant="contained" color="error" onClick={this.doConfirm} disabled={working}>{all ? this.tr('session.revokeAll.button', 'Desconectar de tudo') : this.tr('session.revokeOne.button', 'Encerrar sessão')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { userId, userName, compact } = this.props;
    const { loading, rows, error } = this.state;
    if (!userId) return null;
    const active = rows.filter((r) => r.active);
    const allButton = (
      <Button size="small" variant="outlined" color="error" startIcon={<PowerSettingsNewIcon />} disabled={loading || !active.length} onClick={() => this.setState({ confirm: { kind: 'all' }, reason: '' })}>
        {this.tr('session.revokeAll.button', 'Desconectar de tudo')}{active.length ? ` (${active.length})` : ''}
      </Button>
    );
    if (compact) return <span>{allButton}{this.renderConfirm()}</span>;
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 12 }}><CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{this.tr('session.title', 'Sessões')}{userName ? ` · ${userName}` : ''}</Typography>
            <Typography variant="caption" style={{ color: '#888' }}>{this.tr('session.subtitle', 'uma linha por login · ativa = ainda pode renovar (24 h do login, 1 h sem uso) · as recentes já encerradas ficam para conferência')}</Typography>
          </Box>
          {allButton}
        </Box>
        {loading && <Box display="flex" justifyContent="center" style={{ padding: 12 }}><CircularProgress size={22} color="secondary" /></Box>}
        {!loading && error && <Typography variant="body2" style={{ color: '#c62828', marginTop: 8 }}>{error}</Typography>}
        {!loading && !error && !rows.length && <Typography variant="body2" style={{ color: '#888', marginTop: 8 }}>{this.tr('session.none', 'Nenhuma sessão registrada para este usuário (as sessões passaram a ser gravadas em 23/09/2026).')}</Typography>}
        {!loading && rows.length > 0 && (
          <Box style={{ overflowX: 'auto', marginTop: 8 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('session.col.status', 'Estado')}</TableCell><TableCell>{this.tr('session.col.started', 'Início')}</TableCell><TableCell>{this.tr('session.col.lastRefresh', 'Última renovação')}</TableCell>
                <TableCell>{this.tr('session.col.expires', 'Vence em')}</TableCell><TableCell>IP</TableCell><TableCell>{this.tr('session.col.browser', 'Navegador')}</TableCell><TableCell>{this.tr('session.col.end', 'Encerramento')}</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.sessionId} hover style={{ opacity: r.active ? 1 : 0.6, backgroundColor: r.current ? 'rgba(139,195,74,0.10)' : undefined, boxShadow: r.current ? 'inset 4px 0 0 #8BC34A' : undefined }}>
                    <TableCell>
                      {r.active ? <Chip size="small" label={this.tr('session.status.active', 'ativa')} style={{ backgroundColor: '#2e7d32', color: '#fff', height: 22 }} /> : <Chip size="small" label={this.tr(`session.status.${r.status === 'expired' ? 'expired' : 'revoked'}`, r.status === 'expired' ? 'vencida' : 'derrubada')} style={{ height: 22, backgroundColor: r.status === 'expired' ? '#e0e0e0' : '#ffcdd2' }} />}
                      {r.current && <Chip size="small" label={this.tr('session.current', 'esta')} style={{ marginLeft: 6, height: 22, backgroundColor: '#2e7d32', color: '#fff', fontWeight: 700, letterSpacing: 0.3 }} />}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmt(r.createdAt)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{r.lastRefreshAt ? `${fmt(r.lastRefreshAt)} (${r.refreshCount}×)` : '—'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmt(r.absoluteExpiresAt)}</TableCell>
                    <TableCell style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.ip || '—'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}><DeviceIcon kind={r.deviceKind} intl={this.props.intl} t={t} /><Tooltip title={r.userAgent || ''}><span>{shortUa(r.userAgent)}</span></Tooltip></TableCell>
                    <TableCell>{r.revokedAt ? `${fmt(r.revokedAt)} · ${this.reasonLabel(r.revokeReason)}` : '—'}</TableCell>
                    <TableCell align="right">
                      {r.active && <Button size="small" color="error" startIcon={<LogoutIcon />} onClick={() => this.setState({ confirm: { kind: 'one', sessionId: r.sessionId }, reason: '' })}>{this.tr('session.revokeOne.button', 'Encerrar sessão')}</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {this.renderConfirm()}
      </CardContent></Card>
    );
  }
}
